import type { IntlShape } from 'react-intl';
import type {
    FindingPlanumDocType,
    FindingProfileDocType,
    HistoryVersion
} from '@ast-data/shared';
import { historyEntity, historyFieldLabelId } from '../history/historyEntities';
import {
    historyRecordPath,
    type HistoryRecordDoc
} from '../history/historyRecordPath';
import { planumRefLabel, profileRefLabel } from '../findings/findingRefLabels';
import { locationLabel } from '../findings/locationLabel';
import { formatDate } from '../../utils/formatDate';
import { compareNatural } from '../../utils/compare';
import type { DeliverableGap, DeliverableGapReason } from './deliverableTexts';

/** One record of the warning, with every field of it that is a gap. */
export interface GapRecord {
    key: string;
    ownerType: DeliverableGap['ownerType'];
    record: DeliverableGap['record'];
    fields: { field: string; reason: DeliverableGapReason }[];
}

/** The gaps by record, in the order the export met them. */
export function groupGapsByRecord(
    gaps: readonly DeliverableGap[]
): GapRecord[] {
    const byKey = new Map<string, GapRecord>();
    for (const gap of gaps) {
        const key = `${gap.ownerType}:${gap.ownerId}`;
        const entry = byKey.get(key) ?? {
            key,
            ownerType: gap.ownerType,
            record: gap.record,
            fields: []
        };
        entry.fields.push({ field: gap.field, reason: gap.reason });
        byKey.set(key, entry);
    }
    return [...byKey.values()];
}

function text(record: GapRecord['record'], field: string): string {
    return String(record[field] ?? '');
}

/**
 * What the record is called where it is written elsewhere: a description by its
 * Lage ("Bef. 39, Fl. 1, Pl. 1"), a Befund by its number, everything else by
 * the change log's name for it and its number or date.
 *
 * `findingNumberOf` gives a description its Befund's number; while that is not
 * known, the description is named on its own ("Planum 1").
 */
export function gapRecordLabel(
    intl: IntlShape,
    entry: GapRecord,
    findingNumberOf: (findingId: string) => string | undefined
): string {
    const { ownerType, record } = entry;
    if (ownerType === 'finding') {
        return locationLabel({ findingNumber: text(record, 'findingNumber') });
    }
    const findingNumber = findingNumberOf(text(record, 'findingId'));
    if (ownerType === 'findingPlanum' && findingNumber !== undefined) {
        return planumRefLabel(
            findingNumber,
            record as unknown as FindingPlanumDocType
        );
    }
    if (ownerType === 'findingProfile' && findingNumber !== undefined) {
        return profileRefLabel(
            findingNumber,
            record as unknown as FindingProfileDocType
        );
    }
    const spec = historyEntity(ownerType);
    if (!spec) {
        return ownerType;
    }
    const primary = spec.primaryLabel
        ? spec.primaryLabel(record as unknown as HistoryVersion)
        : spec.primaryField === 'recordedAt'
          ? formatDate(text(record, 'recordedAt'))
          : text(record, spec.primaryField);
    return [intl.formatMessage({ id: spec.nameId }), primary]
        .filter((part) => part !== '')
        .join(' ');
}

/** The field as its form labels it, or its name when no label is known. */
export function gapFieldLabel(
    intl: IntlShape,
    ownerType: GapRecord['ownerType'],
    field: string
): string {
    const spec = historyEntity(ownerType);
    const id = spec ? historyFieldLabelId(spec, field) : '';
    return id && intl.messages[id] ? intl.formatMessage({ id }) : field;
}

/** A Befund and its descriptions are one block; every other kind its own. */
function kindOf(ownerType: GapRecord['ownerType']): string {
    return ownerType === 'findingPlanum' || ownerType === 'findingProfile'
        ? 'finding'
        : ownerType;
}

/**
 * The warning's order: Befunde first, then each other kind of record in a
 * block, and within a block as the records are numbered — "Bef. 2" before
 * "Bef. 10". A Tagebuch day is named by a German date, which would sort by
 * day of the month, so days go by their stored date instead.
 */
export function sortGapRecords<Row extends { entry: GapRecord; label: string }>(
    rows: readonly Row[]
): Row[] {
    const kindKey = (row: Row) => {
        const kind = kindOf(row.entry.ownerType);
        return kind === 'finding' ? '' : kind;
    };
    const sortKey = (row: Row) =>
        row.entry.ownerType === 'diary'
            ? text(row.entry.record, 'recordedAt')
            : row.label;
    return [...rows].sort(
        (firstRow, secondRow) =>
            kindKey(firstRow).localeCompare(kindKey(secondRow)) ||
            compareNatural(sortKey(firstRow), sortKey(secondRow))
    );
}

/** The office screen of the record, or null when it has none. */
export function gapRecordPath(entry: GapRecord): string | null {
    return historyRecordPath(
        entry.ownerType,
        entry.record as unknown as HistoryRecordDoc
    );
}
